import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';

export interface InsightAction {
    title: string;
    priority: string;
}

export interface Insight {
    category: string;
    title: string;
    body: string;
    severity: string;

    metric?: {
        value: number;
        unit: string;
        benchmark: string;
    };

    actions: InsightAction[];
}

export interface PerformanceResponse {
    metricModel: {
        averageWeight: number;
        target: number;
        currentFeedingConsumption: string;
        feedingStatus: string;
        averageWeightStatus: string;
    };

    mortalityRate: {
        totalBirds: number;
        mortality: number;
        mortalityRate: number;
        mortalityRateStatus: string;
    };

    insights: Insight[];
}

@Component({
    selector: 'app-performance-record',
    standalone: true,
    imports: [CommonModule],
    templateUrl: './performance-record.component.html',
    styleUrl: './performance-record.component.scss'
})
export class PerformanceRecordComponent {

    @Input() set apiResponse(value: any) {
        if (value) {
            this.performance = this.mapApiResponse(value);
        }
    }

    performance: PerformanceResponse | null = null;

    mapApiResponse(api: any): PerformanceResponse {

        const mapInsight = (item: any, category: string): Insight => ({
            category,
            title: item.title,
            body: item.body,
            severity: item.severity,
            metric: item.metricValue != null ? {
                value: item.metricValue,
                unit: item.unit,
                benchmark: item.benchmark
            } : undefined,
            actions: item.actions || []
        });

        return {
            metricModel: {
                ...api.metricModel,
                feedingStatus: api.metricModel?.feedingStatus,
                averageWeightStatus: api.metricModel?.averageWeightStatus
            },
            mortalityRate: {
                ...api.mortalityRate,
                mortalityRateStatus: api.mortalityRate?.mortalityRateStatus
            },
            insights: [
                ...(api.feedingInsights || []).map((x: any) => mapInsight(x, 'feed')),
                ...(api.weightInsights || []).map((x: any) => mapInsight(x, 'weight')),
                ...(api.mortalityInsights || []).map((x: any) => mapInsight(x, 'mortality'))
            ]
        };
    }

    getInsightsByCategory(category: string): Insight[] {
        return this.performance?.insights?.filter(x => x.category === category) || [];
    }

    getTopInsight(category: string): Insight | null {
        const data = this.getInsightsByCategory(category);
        return data.length ? data[0] : null;
    }

    getKpiStatusClass(status: string): string {
        switch (status?.toLowerCase()) {
            case 'poor':
                return 'badge-poor';
            case 'warning':
                return 'badge-warning';
            case 'good':
                return 'badge-good';
            default:
                return 'badge-default';
        }
    }

    getKpiStatusLabel(status: string): string {
        if (!status) return 'N/A';
        return status;
    }

    getSeverityClass(severity: string): string {
        switch (severity?.toLowerCase()) {
            case 'critical':
                return 'badge-poor';
            case 'warning':
                return 'badge-warning';
            case 'info':
                return 'badge-good';
            default:
                return 'badge-default';
        }
    }

    getSeverityLabel(severity: string): string {
        if (!severity) return 'N/A';
        return severity.charAt(0).toUpperCase() + severity.slice(1);
    }
}